import { useMemo, useState, type ReactNode } from 'react';
import { AROMA_FAMILIES, type AromaFamily, type ReferenceTermView } from '@cercle/shared';
import { AromaWheel } from '@/shared/reference/AromaWheel';
import { useReference } from '@/shared/reference/useReference';
import { EmptyState, Kicker, Muted, Skeleton, Tag } from '@/shared/ui';
import styles from './aromas.module.scss';

/**
 * Écran 14 — la roue des arômes.
 *
 * Le vocabulaire du cercle, famille par famille. On tourne la roue, on lit ce que les
 * membres emploient réellement, et l'on voit combien de fois chaque descripteur a servi.
 *
 * C'est un écran de lecture, pas de saisie : l'écran 04 a désormais la même roue, mais
 * sélectionnable. Ce qui reste propre à celui-ci, c'est le compte d'emplois — savoir
 * qu'« Agrumes » a servi douze fois et « Rancio » jamais n'a pas sa place au milieu d'une
 * dégustation, et c'est pourtant ce qui raconte le vocabulaire du cercle.
 */
const FAMILIES = Object.keys(AROMA_FAMILIES) as AromaFamily[];

export function AromaWheelScreen(): ReactNode {
  const { terms, loading } = useReference('AROMA');
  const [index, setIndex] = useState(0);

  const byFamily = useMemo(() => {
    const map = new Map<string, ReferenceTermView[]>(FAMILIES.map((family) => [family, []]));
    for (const term of terms) {
      const family = term.family && map.has(term.family) ? term.family : 'Technique';
      map.get(family)?.push(term);
    }
    // Le plus employé en tête : c'est le vocabulaire vivant du cercle.
    for (const list of map.values()) list.sort((a, b) => b.usageCount - a.usageCount);
    return map;
  }, [terms]);

  // `FAMILIES` n'est jamais vide — les six familles sont dans le contrat — mais rien dans
  // le type ne le dit, et un index hors bornes doit retomber quelque part de sensé.
  const family = FAMILIES[index] ?? 'Technique';
  const current = byFamily.get(family) ?? [];
  const used = current.filter((term) => term.usageCount > 0).length;

  return (
    <div className={styles.page}>
      <header>
        <Kicker>Roue des arômes</Kicker>
        <h1>Le vocabulaire du cercle</h1>
        <Muted>
          Tournez la roue — au doigt, ou aux flèches du clavier — pour parcourir les six
          familles.
        </Muted>
      </header>

      <AromaWheel
        families={FAMILIES}
        index={index}
        onChange={setIndex}
        caption={`${current.length} descripteur${current.length > 1 ? 's' : ''}`}
      />

      <section aria-live="polite">
        <h2 className={styles.sectionTitle}>
          {family} <Muted>· {used} déjà employé{used > 1 ? 's' : ''}</Muted>
        </h2>

        {loading && terms.length === 0 ? <Skeleton lines={3} /> : null}
        {!loading && current.length === 0 ? (
          <EmptyState>Cette famille n’a pas encore de descripteur.</EmptyState>
        ) : null}

        <ul className={styles.terms}>
          {current.map((term) => (
            <li key={term.id}>
              {/*
                Un descripteur que personne n'a encore employé reste en sourdine : il
                existe, mais il ne fait pas partie du vocabulaire du cercle. Ceux qui
                servent passent en or — l'or ne désigne que ce qui a de la valeur.
              */}
              <Tag gold={term.usageCount > 0}>
                {term.label}
                {term.usageCount > 0 ? <span className={styles.count}>{term.usageCount}</span> : null}
              </Tag>
            </li>
          ))}
        </ul>
      </section>

      <Muted>
        Un descripteur manque ? Il s’ajoute au moment de noter, sur la même roue, dans la
        famille sous le repère — c’est là qu’on sait s’il est boisé ou fruité.
      </Muted>
    </div>
  );
}
